import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Textarea,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/toast";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { ScriptTagSettingTabContainer_site$key } from "~/src/__relay_artifacts__/ScriptTagSettingTabContainer_site.graphql";
import { ScriptTagSettingTabContainer_ToggleModuleMutation } from "~/src/__relay_artifacts__/ScriptTagSettingTabContainer_ToggleModuleMutation.graphql";
import { ScriptTagSettingTabContainer_UpdateModuleMutation } from "~/src/__relay_artifacts__/ScriptTagSettingTabContainer_UpdateModuleMutation.graphql";
import { Tab } from "~/src/components/common/Tab";
import { useQueryString } from "~/src/lib/hooks";
import { useMutationCommit } from "~/src/lib/react-relay";
import { userTagModuleStatusLabel } from "~/src/utils/userTagModuleFormatters";

import {
  type CompiledSnapshot,
  type LinkedModuleDiffItem,
  ModuleEditorModal,
  ModuleListTab,
  OverviewTab,
  TagDiffModal,
  type UserTagVersionCompareItem,
  VersionComparePanel,
  VersionHistoryTab,
  type VersionItem,
  type VersionModuleSnapshotRow,
} from "./presentations";
import { buildLinkedModuleDiffsFromSnapshots } from "./presentations/buildLinkedModuleDiffsFromSnapshots";
import { DiffLine, computeDiff } from "./presentations/computeDiff";
import { extractEs5LintError } from "./presentations/es5LintError";
import {
  type LabelledModuleChange,
  type ModuleForPendingSummary,
  type WorkspacePendingSummary,
  buildWorkspacePendingSummary,
} from "./presentations/moduleWorkspacePendingSummary";
import { PendingChangeSummaryLists } from "./presentations/PendingChangeSummaryLists";
import type { DistributionGroupOption } from "./presentations/UserTagModuleLinkFields";

export type Props = {
  siteRef: ScriptTagSettingTabContainer_site$key;
};

const fragment = graphql`
  fragment ScriptTagSettingTabContainer_site on Site {
    id
    embedTag
    robeeTag
    userTagDraft
    userTagPublishedModule {
      id
      versionNumber
      tagContent
      status
      description
      authorName
      publishedAt
      createdAt
    }
    userTagModules {
      id
      versionNumber
      tagContent
      status
      description
      authorName
      publishedAt
      createdAt
      # New module fields
      name
      enabled
      priority
      draftContent
      draftEnabled
      hasDraft
      hasPendingToggle
      hasPendingChanges
      effectiveEnabled
      isDistributionGated
      isDraftDistributionGated
      hasPendingLinkChange
      isArchive
      deletedAt
      popupGroups {
        id
        name
      }
      abtestScenarioPageGroups {
        id
        title
      }
      draftPopupGroups {
        id
        name
      }
      draftAbtestScenarioPageGroups {
        id
        title
      }
    }
    popupGroups(first: 500) {
      edges {
        node {
          id
          name
          page {
            funnel {
              id
              name
            }
          }
        }
      }
    }
    abtestScenarioPageGroups(first: 500) {
      edges {
        node {
          id
          title
          page {
            funnel {
              id
              name
            }
          }
        }
      }
    }
    # New fields for module system
    enabledUserTagModules {
      id
      name
      enabled
      priority
      tagContent
    }
    mergedTagPreview
    draftMergedTagPreview
    enabledModuleCount
    totalModuleCount
    hasPendingChanges
    hasWorkspaceDeployDrift
    userTagWorkspacePendingChangeSummary {
      codeDraftModuleNames
      addedModuleNames
      removedModuleNames
      toggleChanges {
        moduleName
        fromLabel
        toLabel
      }
      linkChanges {
        moduleName
        fromLabel
        toLabel
      }
    }
    userTagVersions {
      id
      versionName
      description
      authorName
      isActive
      createdAt
      content
      changeSummary {
        codeDraftModuleNames
        addedModuleNames
        removedModuleNames
        toggleChanges {
          moduleName
          fromLabel
          toLabel
        }
        linkChanges {
          moduleName
          fromLabel
          toLabel
        }
      }
      modulesSnapshot {
        moduleId
        name
        enabled
        priority
        gated
        tagContent
      }
    }
    activeUserTagVersion {
      id
      versionName
      description
      authorName
      isActive
      createdAt
    }
  }
`;

// New module mutations
const createModuleMutation = graphql`
  mutation ScriptTagSettingTabContainer_CreateModuleMutation(
    $siteId: ID!
    $name: String!
    $tagContent: String
    $description: String
    $priority: Int
    $popupGroupIds: [ID!]
    $abtestScenarioPageGroupIds: [ID!]
  ) {
    createUserTagModule(
      input: {
        siteId: $siteId
        name: $name
        tagContent: $tagContent
        description: $description
        priority: $priority
        popupGroupIds: $popupGroupIds
        abtestScenarioPageGroupIds: $abtestScenarioPageGroupIds
      }
    ) {
      site {
        ...ScriptTagSettingTabContainer_site
      }
      userTagModule {
        id
        name
        enabled
        priority
        tagContent
        hasDraft
      }
    }
  }
`;

const toggleModuleMutation = graphql`
  mutation ScriptTagSettingTabContainer_ToggleModuleMutation(
    $siteId: ID!
    $moduleId: ID!
    $enabled: Boolean!
  ) {
    toggleUserTagModule(
      input: { siteId: $siteId, moduleId: $moduleId, enabled: $enabled }
    ) {
      site {
        ...ScriptTagSettingTabContainer_site
      }
      userTagModule {
        id
        enabled
        hasPendingToggle
      }
    }
  }
`;

const updateModuleMutation = graphql`
  mutation ScriptTagSettingTabContainer_UpdateModuleMutation(
    $siteId: ID!
    $moduleId: ID!
    $name: String
    $description: String
    $popupGroupIds: [ID!]
    $abtestScenarioPageGroupIds: [ID!]
  ) {
    updateUserTagModule(
      input: {
        siteId: $siteId
        moduleId: $moduleId
        name: $name
        description: $description
        popupGroupIds: $popupGroupIds
        abtestScenarioPageGroupIds: $abtestScenarioPageGroupIds
      }
    ) {
      site {
        ...ScriptTagSettingTabContainer_site
      }
      userTagModule {
        id
        name
        description
        hasPendingLinkChange
      }
    }
  }
`;

const deleteModuleMutation = graphql`
  mutation ScriptTagSettingTabContainer_DeleteModuleMutation(
    $siteId: ID!
    $moduleId: ID!
  ) {
    deleteUserTagModule(input: { siteId: $siteId, moduleId: $moduleId }) {
      site {
        ...ScriptTagSettingTabContainer_site
      }
      deletedModuleId
    }
  }
`;

const saveModuleDraftMutation = graphql`
  mutation ScriptTagSettingTabContainer_SaveModuleDraftMutation(
    $siteId: ID!
    $moduleId: ID!
    $draftContent: String!
  ) {
    saveUserTagModuleDraft(
      input: {
        siteId: $siteId
        moduleId: $moduleId
        draftContent: $draftContent
      }
    ) {
      site {
        ...ScriptTagSettingTabContainer_site
      }
      userTagModule {
        id
        draftContent
        hasDraft
        hasPendingChanges
      }
    }
  }
`;

const discardModuleDraftMutation = graphql`
  mutation ScriptTagSettingTabContainer_DiscardModuleDraftMutation(
    $siteId: ID!
    $moduleId: ID!
  ) {
    discardUserTagModuleDraft(input: { siteId: $siteId, moduleId: $moduleId }) {
      site {
        ...ScriptTagSettingTabContainer_site
      }
      userTagModule {
        id
        draftContent
        draftEnabled
        hasDraft
        hasPendingToggle
        hasPendingChanges
      }
    }
  }
`;

const activateUserTagVersionMutation = graphql`
  mutation ScriptTagSettingTabContainer_ActivateUserTagVersionMutation(
    $versionId: ID!
  ) {
    activateUserTagVersion(input: { versionId: $versionId }) {
      site {
        ...ScriptTagSettingTabContainer_site
      }
      userTagVersion {
        id
        versionName
        isActive
        content
        createdAt
      }
    }
  }
`;

const discardAllPendingModuleChangesMutation = graphql`
  mutation ScriptTagSettingTabContainer_DiscardAllPendingModuleChangesMutation(
    $siteId: ID!
  ) {
    discardAllPendingModuleChanges(input: { siteId: $siteId }) {
      site {
        ...ScriptTagSettingTabContainer_site
      }
    }
  }
`;

const publishUserTagWorkspaceMutation = graphql`
  mutation ScriptTagSettingTabContainer_PublishCompiledModuleWorkspaceMutation(
    $siteId: ID!
    $versionName: String!
    $description: String
  ) {
    publishUserTagWorkspace(
      input: {
        siteId: $siteId
        versionName: $versionName
        description: $description
      }
    ) {
      site {
        ...ScriptTagSettingTabContainer_site
      }
      userTagVersion {
        id
        versionName
      }
    }
  }
`;

type InnerTabId = "published" | "modules" | "history";

const innerTabs = [
  { id: "published" as InnerTabId, label: "概要" },
  { id: "modules" as InnerTabId, label: "モジュール一覧" },
  { id: "history" as InnerTabId, label: "バージョン履歴" },
];

const VERSION_LINKED_DIFF_HELP =
  "配信条件連動モジュールはCDNマージに含まれません。選択した2つのバージョン間のコード差分をモジュールごとに確認できます。";

function mapVersionModulesSnapshot(
  rows:
    | ReadonlyArray<{
        readonly moduleId: number;
        readonly name: string | null;
        readonly enabled: boolean;
        readonly priority: number;
        readonly gated: boolean | null;
        readonly tagContent: string | null;
      }>
    | null
    | undefined
): VersionModuleSnapshotRow[] | null {
  if (rows == null) return null;
  return rows.map((r) => ({
    id: r.moduleId,
    name: r.name,
    enabled: r.enabled,
    priority: r.priority,
    gated: r.gated,
    tagContent: r.tagContent,
  }));
}

type LabelledChangeRows = ReadonlyArray<{
  readonly moduleName: string;
  readonly fromLabel: string;
  readonly toLabel: string;
}>;

const mapLabelledChanges = (rows: LabelledChangeRows): LabelledModuleChange[] =>
  rows.map((row) => ({
    moduleName: row.moduleName,
    fromLabel: row.fromLabel,
    toLabel: row.toLabel,
  }));

function mapVersionChangeSummary(
  summary:
    | {
        readonly codeDraftModuleNames: ReadonlyArray<string>;
        readonly addedModuleNames: ReadonlyArray<string>;
        readonly removedModuleNames: ReadonlyArray<string>;
        readonly toggleChanges: LabelledChangeRows;
        readonly linkChanges: LabelledChangeRows;
      }
    | null
    | undefined
): WorkspacePendingSummary | null {
  if (summary == null) return null;
  return {
    codeDraftModuleNames: [...summary.codeDraftModuleNames],
    addedModuleNames: [...summary.addedModuleNames],
    removedModuleNames: [...summary.removedModuleNames],
    toggleChanges: mapLabelledChanges(summary.toggleChanges),
    linkChanges: mapLabelledChanges(summary.linkChanges),
  };
}

export const ScriptTagSettingTabContainer: FC<
  React.PropsWithChildren<Props>
> = ({ siteRef }) => {
  const site = useFragment(fragment, siteRef);
  const toast = useToast();
  const {
    params: { moduleId: moduleIdFromUrl },
    changeQueryString,
  } = useQueryString<{ tab?: string; moduleId?: string }>();

  const [currentTabId, setCurrentTabId] = useState<InnerTabId>("published");
  const onTabChange = useCallback((id: InnerTabId) => setCurrentTabId(id), []);

  const [diffLines, setDiffLines] = useState<DiffLine[] | null>(null);
  const [diffColumnLabels, setDiffColumnLabels] = useState({
    left: "比較元",
    right: "比較先",
  });
  const [compareDiffDescriptions, setCompareDiffDescriptions] = useState<{
    left: string | null;
    right: string | null;
  }>({ left: null, right: null });
  /** When non-null, compare/snapshot TagDiffModal shows two-tab linked diffs. */
  const [historyLinkedModuleDiffs, setHistoryLinkedModuleDiffs] = useState<
    LinkedModuleDiffItem[] | null
  >(null);
  const [historyLinkedColumnLabels, setHistoryLinkedColumnLabels] = useState({
    left: "比較元",
    right: "比較先",
  });
  const [publishVersionName, setPublishVersionName] = useState("");
  const [publishDescription, setPublishDescription] = useState("");
  const [isPublishingWorkspace, setIsPublishingWorkspace] = useState(false);
  const [isDiscardingWorkspace, setIsDiscardingWorkspace] = useState(false);
  const [versionCompareBase, setVersionCompareBase] =
    useState<UserTagVersionCompareItem | null>(null);
  const [versionCompareTarget, setVersionCompareTarget] =
    useState<UserTagVersionCompareItem | null>(null);

  // Module editor state
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const openedModuleIdFromUrlRef = useRef<string | null>(null);

  const {
    isOpen: isWorkspacePublishOpen,
    onOpen: onWorkspacePublishOpen,
    onClose: onWorkspacePublishClose,
  } = useDisclosure();
  const {
    isOpen: isWorkspaceCancelOpen,
    onOpen: onWorkspaceCancelOpen,
    onClose: onWorkspaceCancelClose,
  } = useDisclosure();
  const {
    isOpen: isCompareDiffOpen,
    onOpen: onCompareDiffOpen,
    onClose: onCompareDiffCloseInternal,
  } = useDisclosure();
  const {
    isOpen: isVersionCompareModalOpen,
    onOpen: onVersionCompareModalOpen,
    onClose: onVersionCompareModalClose,
  } = useDisclosure();

  const onCompareDiffClose = useCallback(() => {
    setCompareDiffDescriptions({ left: null, right: null });
    setHistoryLinkedModuleDiffs(null);
    setSnapshotForRollback(null);
    onCompareDiffCloseInternal();
  }, [onCompareDiffCloseInternal]);
  const workspaceCancelConfirmRef = useRef(null);

  // New module mutation commits
  const commitCreateModule = useMutationCommit(createModuleMutation);
  const commitToggleModule =
    useMutationCommit<ScriptTagSettingTabContainer_ToggleModuleMutation>(
      toggleModuleMutation
    );
  const commitUpdateModule =
    useMutationCommit<ScriptTagSettingTabContainer_UpdateModuleMutation>(
      updateModuleMutation
    );
  const commitDeleteModule = useMutationCommit(deleteModuleMutation);

  // New draft/publish mutation commits
  const commitSaveModuleDraft = useMutationCommit(saveModuleDraftMutation);
  const commitDiscardModuleDraft = useMutationCommit(
    discardModuleDraftMutation
  );
  const commitActivateUserTagVersion = useMutationCommit(
    activateUserTagVersionMutation
  );
  const commitDiscardAllPendingModuleChanges = useMutationCommit(
    discardAllPendingModuleChangesMutation
  );
  const commitPublishCompiledModuleWorkspace = useMutationCommit(
    publishUserTagWorkspaceMutation
  );

  const hasPendingToggleChanges = useMemo(
    () =>
      site.userTagModules.some(
        (v) => v.hasPendingToggle && !v.deletedAt && !v.isArchive
      ),
    [site.userTagModules]
  );
  const hasPendingCodeChanges = useMemo(() => {
    const anyModuleCodeDraft = site.userTagModules.some(
      (v) => v.hasDraft && !v.deletedAt && !v.isArchive
    );
    const compileDriftWithoutToggleOnly =
      site.hasWorkspaceDeployDrift && !hasPendingToggleChanges;
    return anyModuleCodeDraft || compileDriftWithoutToggleOnly;
  }, [
    hasPendingToggleChanges,
    site.hasWorkspaceDeployDrift,
    site.userTagModules,
  ]);

  const workspacePendingSummary = useMemo(() => {
    const s = site.userTagWorkspacePendingChangeSummary;
    if (s) {
      return {
        codeDraftModuleNames: [...s.codeDraftModuleNames],
        addedModuleNames: [...s.addedModuleNames],
        removedModuleNames: [...s.removedModuleNames],
        toggleChanges: mapLabelledChanges(s.toggleChanges),
        linkChanges: mapLabelledChanges(s.linkChanges),
      };
    }
    const modules: ModuleForPendingSummary[] = site.userTagModules
      .filter((v) => !v.deletedAt && !v.isArchive)
      .map((v) => ({
        id: v.id,
        name: v.name,
        createdAt: v.createdAt,
        hasDraft: v.hasDraft,
        hasPendingToggle: v.hasPendingToggle,
        enabled: v.enabled,
        draftEnabled: v.draftEnabled,
        effectiveEnabled: v.effectiveEnabled,
      }));
    return buildWorkspacePendingSummary(
      modules,
      site.activeUserTagVersion?.createdAt ?? null
    );
  }, [site]);

  /**
   * Modules the workspace would publish as 配信条件連動, with a draft, pending toggle,
   * or reserved link change — Tab 2 of publish / compile confirm.
   */
  const linkedModuleDiffs = useMemo<LinkedModuleDiffItem[]>(() => {
    return site.userTagModules
      .filter(
        (v) =>
          v.isDraftDistributionGated &&
          !v.deletedAt &&
          !v.isArchive &&
          (v.hasDraft || v.hasPendingToggle || v.hasPendingLinkChange)
      )
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map((v) => ({
        id: v.id,
        name: v.name,
        left: v.tagContent ?? "",
        right: v.hasDraft ? v.draftContent ?? "" : v.tagContent ?? "",
      }));
  }, [site.userTagModules]);

  /** Enabled / pending gated modules — Tab 2 of コンパイル済みプレビュー. */
  const compiledPreviewLinkedModules = useMemo(() => {
    return site.userTagModules
      .filter(
        (v) =>
          v.isDraftDistributionGated &&
          !v.deletedAt &&
          !v.isArchive &&
          (v.effectiveEnabled ||
            v.hasDraft ||
            v.hasPendingToggle ||
            v.hasPendingLinkChange)
      )
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map((v) => ({
        id: v.id,
        name: v.name,
        content: v.hasDraft ? v.draftContent ?? "" : v.tagContent ?? "",
        hasDraft: v.hasDraft,
        hasPendingToggle: v.hasPendingToggle,
      }));
  }, [site.userTagModules]);

  const handleOpenWorkspaceCancel = useCallback(() => {
    if (!site.hasPendingChanges) {
      toast({
        title: "取り消す変更がありません",
        status: "info",
      });
      return;
    }
    onWorkspaceCancelOpen();
  }, [onWorkspaceCancelOpen, site.hasPendingChanges, toast]);

  const handleConfirmWorkspaceCancel = useCallback(async () => {
    onWorkspaceCancelClose();
    setIsDiscardingWorkspace(true);
    try {
      await commitDiscardAllPendingModuleChanges({
        variables: { siteId: site.id },
      });
      toast({
        title: "変更を取り消しました",
        status: "success",
      });
      setCurrentTabId("modules");
    } catch (_err) {
      toast({
        title: "変更の取り消しに失敗しました",
        status: "error",
      });
    } finally {
      setIsDiscardingWorkspace(false);
    }
  }, [
    commitDiscardAllPendingModuleChanges,
    onWorkspaceCancelClose,
    site.id,
    toast,
  ]);

  const handleOpenWorkspacePublish = useCallback(() => {
    if (!site.hasPendingChanges) {
      toast({
        title: "公開する変更がありません",
        description: "先にモジュールを編集してから、ここから公開してください。",
        status: "info",
      });
      return;
    }
    setPublishVersionName("");
    setPublishDescription("");
    setDiffColumnLabels({
      left: "現在公開中のコンパイル結果",
      right: "下書き（公開後）",
    });
    setCompareDiffDescriptions({ left: null, right: null });
    setDiffLines(
      computeDiff(site.mergedTagPreview ?? "", site.draftMergedTagPreview ?? "")
    );
    onWorkspacePublishOpen();
  }, [
    onWorkspacePublishOpen,
    site.draftMergedTagPreview,
    site.hasPendingChanges,
    site.mergedTagPreview,
    toast,
  ]);

  const handleConfirmWorkspacePublish = useCallback(async () => {
    const name = publishVersionName.trim();
    if (!name) {
      return;
    }
    if (!site.hasPendingChanges) {
      toast({
        title: "公開する変更がありません",
        status: "info",
      });
      onWorkspacePublishClose();
      return;
    }

    setIsPublishingWorkspace(true);
    try {
      await commitPublishCompiledModuleWorkspace({
        variables: {
          siteId: site.id,
          versionName: name,
          description: publishDescription.trim() || undefined,
        },
      });
      toast({
        title: "変更を公開しました",
        status: "success",
      });
      setPublishVersionName("");
      setPublishDescription("");
      onWorkspacePublishClose();
      setCurrentTabId("published");
    } catch (_err) {
      toast({
        title: "変更の公開に失敗しました",
        status: "error",
      });
    } finally {
      setIsPublishingWorkspace(false);
    }
  }, [
    commitPublishCompiledModuleWorkspace,
    onWorkspacePublishClose,
    publishDescription,
    publishVersionName,
    site.hasPendingChanges,
    site.id,
    toast,
  ]);

  const diffColumnTitle = useCallback((v: VersionItem) => {
    return v.description?.trim() ? v.description : `v${v.versionNumber}`;
  }, []);

  const handleCompareVersions = useCallback(
    (base: VersionItem, compare: VersionItem) => {
      // computeDiff(oldText, newText): left=removed, right=added. Snapshot rollback uses
      // (現在, ロールバック先) — see handleShowSnapshotDiff.
      setDiffColumnLabels({
        left: `${diffColumnTitle(base)}（${userTagModuleStatusLabel(
          base.status
        )}）`,
        right: `${diffColumnTitle(compare)}（${userTagModuleStatusLabel(
          compare.status
        )}）`,
      });
      setCompareDiffDescriptions({
        left: base.description ?? null,
        right: compare.description ?? null,
      });
      setDiffLines(computeDiff(base.tagContent, compare.tagContent));
      onCompareDiffOpen();
    },
    [diffColumnTitle, onCompareDiffOpen]
  );
  const handleVersionCompareSelectionChange = useCallback(
    (
      base: UserTagVersionCompareItem | null,
      compare: UserTagVersionCompareItem | null
    ) => {
      setVersionCompareBase(base);
      setVersionCompareTarget(compare);
    },
    []
  );

  const userTagVersionCompareOptionLabel = useCallback(
    (v: UserTagVersionCompareItem) =>
      `${v.versionName}${v.isActive ? "（現在）" : ""}`,
    []
  );

  const versionComparePanelOnShowDiffNoop = useCallback(
    (_a: UserTagVersionCompareItem, _b: UserTagVersionCompareItem) => {
      // Selection-only mode: in-modal diff is driven by `onSelectionChange`.
    },
    []
  );

  const userTagVersionCompareItems: readonly UserTagVersionCompareItem[] =
    useMemo(
      () =>
        site.userTagVersions.map((v) => ({
          id: v.id,
          sortKey: new Date(v.createdAt).getTime(),
          versionName: v.versionName,
          isActive: v.isActive,
          content: v.content,
          description: v.description ?? null,
          authorName: v.authorName ?? null,
          createdAt: v.createdAt,
          modulesSnapshot: mapVersionModulesSnapshot(v.modulesSnapshot),
        })),
      [site.userTagVersions]
    );
  const versionCompareDiffLines = useMemo(() => {
    if (!versionCompareBase || !versionCompareTarget) return [];
    return computeDiff(
      versionCompareBase.content,
      versionCompareTarget.content
    );
  }, [versionCompareBase, versionCompareTarget]);
  const versionCompareLinkedModuleDiffs = useMemo(
    () =>
      buildLinkedModuleDiffsFromSnapshots(
        versionCompareBase?.modulesSnapshot,
        versionCompareTarget?.modulesSnapshot
      ),
    [versionCompareBase, versionCompareTarget]
  );
  const versionCompareLabels = useMemo(() => {
    const label = (v: UserTagVersionCompareItem) =>
      `${v.versionName}${v.isActive ? "（現在）" : ""}`;
    return {
      left: versionCompareBase ? label(versionCompareBase) : "比較元",
      right: versionCompareTarget ? label(versionCompareTarget) : "比較先",
    };
  }, [versionCompareBase, versionCompareTarget]);

  const compiledSnapshots: CompiledSnapshot[] = useMemo(
    () =>
      site.userTagVersions.map((s) => ({
        id: s.id,
        versionName: s.versionName,
        description: s.description || undefined,
        authorName: s.authorName || undefined,
        isActive: s.isActive,
        createdAt: s.createdAt,
        content: s.content || undefined,
        changeSummary: mapVersionChangeSummary(s.changeSummary),
        modulesSnapshot: mapVersionModulesSnapshot(s.modulesSnapshot),
      })),
    [site.userTagVersions]
  );

  // Module handlers - actual implementations
  const handleModuleToggle = useCallback(
    async (moduleId: string, enabled: boolean) => {
      try {
        const response = await commitToggleModule({
          variables: {
            siteId: site.id,
            moduleId,
            enabled,
          },
        });
        const stillPending =
          response.toggleUserTagModule?.userTagModule?.hasPendingToggle ===
          true;
        if (stillPending) {
          toast({
            title: enabled
              ? "モジュールの有効化を予約しました"
              : "モジュールの無効化を予約しました",
            description: "公開するまで変更は反映されません",
            status: "info",
          });
        } else {
          toast({
            title: "有効/無効の予約を取り消しました",
            status: "success",
          });
        }
      } catch (error) {
        toast({
          title: "モジュールの切り替えに失敗しました",
          status: "error",
        });
      }
    },
    [commitToggleModule, site.id, toast]
  );

  const handleModuleEdit = useCallback(
    async (
      moduleId: string,
      updates: {
        name?: string;
        description?: string;
        popupGroupIds?: string[];
        abtestScenarioPageGroupIds?: string[];
      }
    ) => {
      try {
        await commitUpdateModule({
          variables: {
            siteId: site.id,
            moduleId,
            ...updates,
          },
        });
        toast({
          title: "モジュールを更新しました",
          status: "success",
        });
      } catch (error) {
        toast({
          title: "モジュールの更新に失敗しました",
          status: "error",
        });
      }
    },
    [commitUpdateModule, site.id, toast]
  );

  const handleModuleDelete = useCallback(
    async (moduleId: string) => {
      try {
        await commitDeleteModule({
          variables: {
            siteId: site.id,
            moduleId,
          },
        });
        toast({
          title: "削除を次の一括公開まで保留にしました",
          status: "success",
        });
      } catch (error) {
        toast({
          title: "モジュールの削除に失敗しました",
          status: "error",
        });
      }
    },
    [commitDeleteModule, site.id, toast]
  );

  const handleModuleReorder = useCallback(
    (moduleOrders: { moduleId: string; priority: number }[]) => {
      console.log("Reorder modules", moduleOrders);
      // TODO: Implement reorderUserTagModules mutation
    },
    []
  );

  const handleModuleCreate = useCallback(
    async (name: string, content: string, description: string | undefined) => {
      try {
        await commitCreateModule({
          variables: {
            siteId: site.id,
            name,
            tagContent: content || "",
            description: description || null,
            priority: 0,
          },
        });
        toast({
          title: "モジュールを作成しました",
          status: "success",
        });
      } catch (error) {
        const es5Error = extractEs5LintError(error);
        toast({
          title: es5Error?.message || "モジュールの作成に失敗しました",
          status: "error",
        });
        throw error;
      }
    },
    [commitCreateModule, site.id, toast]
  );

  const handleModuleLink = useCallback(
    async (
      moduleId: string,
      popupGroupIds: string[],
      abtestScenarioPageGroupIds: string[]
    ) => {
      try {
        const response = await commitUpdateModule({
          variables: {
            siteId: site.id,
            moduleId,
            popupGroupIds,
            abtestScenarioPageGroupIds,
          },
        });
        const isReserved =
          response.updateUserTagModule?.userTagModule?.hasPendingLinkChange ===
          true;
        if (isReserved) {
          toast({
            title: "紐付けの変更を予約しました",
            description: "公開するまで配信には反映されません",
            status: "info",
          });
        } else {
          toast({
            title: "紐付けの予約を取り消しました",
            status: "success",
          });
        }
      } catch (error) {
        toast({
          title: "紐付けの更新に失敗しました",
          status: "error",
        });
        throw error;
      }
    },
    [commitUpdateModule, site.id, toast]
  );

  const handleOpenModuleEditor = useCallback(
    (mod: { id: string; deletedAt?: string | null }) => {
      if (mod.deletedAt) {
        return;
      }
      openedModuleIdFromUrlRef.current = mod.id;
      setCurrentTabId("modules");
      setEditingModuleId(mod.id);
      changeQueryString({ tab: "script_tag", moduleId: mod.id });
    },
    [changeQueryString]
  );

  // Module editor handlers
  const handleModuleSaveDraft = useCallback(
    async (moduleId: string, content: string) => {
      try {
        await commitSaveModuleDraft({
          variables: {
            siteId: site.id,
            moduleId,
            draftContent: content,
          },
        });
        toast({
          title: "保存しました",
          status: "success",
        });
      } catch (error) {
        const es5Error = extractEs5LintError(error);
        toast({
          title: es5Error?.message || "保存に失敗しました",
          status: "error",
        });
        throw error;
      }
    },
    [commitSaveModuleDraft, site.id, toast]
  );

  const handleModuleDiscardDraft = useCallback(
    async (moduleId: string) => {
      try {
        await commitDiscardModuleDraft({
          variables: {
            siteId: site.id,
            moduleId,
          },
        });
        toast({
          title: "下書きを破棄しました",
          status: "info",
        });
      } catch (error) {
        toast({
          title: "下書きの破棄に失敗しました",
          status: "error",
        });
        throw error;
      }
    },
    [commitDiscardModuleDraft, site.id, toast]
  );

  const handleModuleShowDiff = useCallback(
    (moduleId: string) => {
      const module = site.userTagModules.find((v) => v.id === moduleId);
      if (!module || !module.hasDraft) return;

      // Module draft vs published — single SDK tab only (not version history tabs).
      setHistoryLinkedModuleDiffs(null);

      // Create mock version items for the diff modal
      const publishedVersion = {
        id: `${moduleId}-published`,
        versionNumber: 1,
        status: "published" as const,
        tagContent: module.tagContent || "",
        description: module.name,
        authorName: "",
        publishedAt: "",
        createdAt: "",
      };

      const draftVersion = {
        id: `${moduleId}-draft`,
        versionNumber: 2,
        status: "draft" as const,
        tagContent: module.draftContent || "",
        description: module.name,
        authorName: "",
        publishedAt: "",
        createdAt: "",
      };

      handleCompareVersions(publishedVersion, draftVersion);
    },
    [site.userTagModules, handleCompareVersions]
  );

  const [snapshotOperationInProgress, setSnapshotOperationInProgress] =
    useState<string | null>(null);
  const [snapshotForRollback, setSnapshotForRollback] = useState<{
    id: string;
    versionName: string;
  } | null>(null);

  const handleActivateSnapshot = useCallback(
    async (snapshotId: string) => {
      const targetSnapshot = site.userTagVersions.find(
        (s) => s.id === snapshotId
      );
      if (!targetSnapshot) return;

      setSnapshotOperationInProgress(snapshotId);
      try {
        await commitActivateUserTagVersion({
          variables: {
            versionId: snapshotId,
          },
        });

        toast({
          title: "スナップショットを復元しました",
          description: `${targetSnapshot.versionName} の内容に戻しました`,
          status: "success",
        });
      } catch (error) {
        console.error("Failed to restore snapshot:", error);
        toast({
          title: "ロールバックに失敗しました",
          description: "しばらく待ってから再試行してください",
          status: "error",
        });
      } finally {
        setSnapshotOperationInProgress(null);
      }
    },
    [commitActivateUserTagVersion, site.userTagVersions, toast]
  );

  const handleShowSnapshotDiff = useCallback(
    (snapshotA: CompiledSnapshot, snapshotB: CompiledSnapshot) => {
      // snapshotA = row the user selected (ロールバック先), snapshotB = 現在アクティブ
      // Diff: old = 現在, new = ロールバック先 → green/right shows lines that reappear on restore
      const versionA = {
        id: snapshotA.id,
        versionNumber: 1,
        status: "published" as const,
        tagContent: snapshotA.content || "",
        description: snapshotA.versionName,
        authorName: snapshotA.authorName || "",
        publishedAt: snapshotA.createdAt,
        createdAt: snapshotA.createdAt,
      };

      const versionB = {
        id: snapshotB.id,
        versionNumber: 2,
        status: "published" as const,
        tagContent: snapshotB.content || "",
        description: snapshotB.versionName,
        authorName: snapshotB.authorName || "",
        publishedAt: snapshotB.createdAt,
        createdAt: snapshotB.createdAt,
      };

      setSnapshotForRollback({
        id: snapshotA.id,
        versionName: snapshotA.versionName,
      });

      const leftLabel = `${snapshotB.versionName}${
        snapshotB.isActive ? "（現在）" : ""
      }`;
      const rightLabel = `${snapshotA.versionName}`;
      setHistoryLinkedColumnLabels({ left: leftLabel, right: rightLabel });
      setHistoryLinkedModuleDiffs(
        buildLinkedModuleDiffsFromSnapshots(
          snapshotB.modulesSnapshot,
          snapshotA.modulesSnapshot
        )
      );

      handleCompareVersions(versionB, versionA);
    },
    [handleCompareVersions]
  );

  const handleSnapshotDiffRollback = useCallback(async () => {
    if (!snapshotForRollback) return;

    await handleActivateSnapshot(snapshotForRollback.id);
    onCompareDiffClose();
    setSnapshotForRollback(null);
  }, [snapshotForRollback, handleActivateSnapshot, onCompareDiffClose]);

  const handleCloseModuleEditor = useCallback(() => {
    if (moduleIdFromUrl) {
      openedModuleIdFromUrlRef.current = moduleIdFromUrl;
    }
    setEditingModuleId(null);
    changeQueryString({ tab: "script_tag" });
  }, [changeQueryString, moduleIdFromUrl]);

  const handlePublishVersionNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPublishVersionName(e.target.value);
    },
    []
  );

  const handlePublishDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setPublishDescription(e.target.value);
    },
    []
  );

  const popupGroupOptions = useMemo<DistributionGroupOption[]>(
    () =>
      (site.popupGroups.edges ?? [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => node != null)
        .map((node) => ({
          id: node.id,
          label: node.name,
          funnelName: node.page.funnel.name,
        })),
    [site.popupGroups.edges]
  );

  const abtestGroupOptions = useMemo<DistributionGroupOption[]>(
    () =>
      (site.abtestScenarioPageGroups.edges ?? [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => node != null)
        .map((node) => ({
          id: node.id,
          label: node.title,
          funnelName: node.page.funnel.name,
        })),
    [site.abtestScenarioPageGroups.edges]
  );

  const editingModule = useMemo(() => {
    if (!editingModuleId) return null;
    const module = site.userTagModules.find((v) => v.id === editingModuleId);
    if (!module) return null;
    if (module.deletedAt) return null;
    return {
      id: module.id,
      name: module.name,
      description: module.description ?? null,
      tagContent: module.tagContent ?? "",
      draftContent: module.draftContent ?? undefined,
      hasDraft: module.hasDraft,
    };
  }, [editingModuleId, site.userTagModules]);

  useEffect(() => {
    if (!moduleIdFromUrl) {
      openedModuleIdFromUrlRef.current = null;
      return;
    }
    if (openedModuleIdFromUrlRef.current === moduleIdFromUrl) {
      return;
    }
    const module = site.userTagModules.find((v) => v.id === moduleIdFromUrl);
    if (!module || module.deletedAt) {
      changeQueryString({ tab: "script_tag" }, true);
      return;
    }
    openedModuleIdFromUrlRef.current = moduleIdFromUrl;
    setCurrentTabId("modules");
    setEditingModuleId(moduleIdFromUrl);
  }, [moduleIdFromUrl, site.userTagModules, changeQueryString]);

  useEffect(() => {
    if (!editingModuleId) {
      return;
    }
    const m = site.userTagModules.find((v) => v.id === editingModuleId);
    if (m?.deletedAt) {
      setEditingModuleId(null);
      changeQueryString({ tab: "script_tag" });
    }
  }, [changeQueryString, editingModuleId, site.userTagModules]);

  return (
    <Box>
      <Tab tabs={innerTabs} currentTabId={currentTabId} onClick={onTabChange} />

      {currentTabId === "published" && (
        <OverviewTab
          embedTag={site.robeeTag}
          mergedTagPreview={site.mergedTagPreview}
          enabledModuleCount={site.enabledModuleCount}
          totalModuleCount={site.totalModuleCount}
          linkedModules={site.userTagModules
            .filter((v) => v.enabled && v.isDistributionGated && !v.deletedAt)
            .slice()
            .sort((a, b) => a.priority - b.priority)
            .map((v) => ({
              id: v.id,
              name: v.name,
              tagContent: v.tagContent,
            }))}
        />
      )}

      {currentTabId === "modules" && (
        <ModuleListTab
          siteId={site.id}
          modules={site.userTagModules.map((v) => ({
            id: v.id,
            name: v.name,
            enabled: v.enabled,
            effectiveEnabled: v.effectiveEnabled,
            priority: v.priority,
            hasDraft: v.hasDraft,
            hasPendingToggle: v.hasPendingToggle,
            isArchive: v.isArchive,
            deletedAt: v.deletedAt,
            tagContent: v.tagContent,
            draftContent: v.draftContent,
            // The list shows the link state the workspace would publish, so a
            // reserved link change moves the row and drives the link modal.
            linkedPopupGroupNames: v.draftPopupGroups.map(
              (group) => group.name
            ),
            linkedAbtestGroupTitles: v.draftAbtestScenarioPageGroups.map(
              (group) => group.title
            ),
            popupGroupIds: v.draftPopupGroups.map((group) => group.id),
            abtestScenarioPageGroupIds: v.draftAbtestScenarioPageGroups.map(
              (group) => group.id
            ),
            isDistributionGated: v.isDraftDistributionGated,
            hasPendingLinkChange: v.hasPendingLinkChange,
          }))}
          onModuleToggle={handleModuleToggle}
          onOpenModuleEditor={handleOpenModuleEditor}
          onModuleDelete={handleModuleDelete}
          onModuleReorder={handleModuleReorder}
          onModuleCreate={handleModuleCreate}
          onModuleLink={handleModuleLink}
          popupGroupOptions={popupGroupOptions}
          abtestGroupOptions={abtestGroupOptions}
          mergedTagPreview={site.mergedTagPreview}
          draftMergedTagPreview={site.draftMergedTagPreview}
          hasPendingChanges={site.hasPendingChanges}
          hasPendingCodeChanges={hasPendingCodeChanges}
          hasPendingToggleChanges={hasPendingToggleChanges}
          enabledModuleCount={site.enabledModuleCount}
          totalModuleCount={site.totalModuleCount}
          gatedModuleCount={
            site.userTagModules.filter((v) => v.isDraftDistributionGated).length
          }
          linkedModules={compiledPreviewLinkedModules}
          onCancelCompiledChanges={handleOpenWorkspaceCancel}
          onPublishCompiledChanges={handleOpenWorkspacePublish}
        />
      )}

      {currentTabId === "history" && (
        <VersionHistoryTab
          compiledSnapshots={compiledSnapshots}
          onActivateSnapshot={handleActivateSnapshot}
          onShowSnapshotDiff={handleShowSnapshotDiff}
          onOpenVersionCompare={onVersionCompareModalOpen}
          isLoading={snapshotOperationInProgress !== null}
        />
      )}

      {/* Module Editor Modal */}
      <ModuleEditorModal
        isOpen={!!editingModuleId}
        onClose={handleCloseModuleEditor}
        module={editingModule}
        onSaveMetadata={handleModuleEdit}
        onSaveDraft={handleModuleSaveDraft}
        onDiscardDraft={handleModuleDiscardDraft}
        onShowDiff={handleModuleShowDiff}
      />

      {isWorkspacePublishOpen && (
        <TagDiffModal
          isOpen
          layout="fullscreen"
          onClose={onWorkspacePublishClose}
          onConfirm={handleConfirmWorkspacePublish}
          title="変更を公開"
          prependBody={
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">バージョン名</FormLabel>
                <Input
                  size="sm"
                  value={publishVersionName}
                  onChange={handlePublishVersionNameChange}
                  placeholder="例: v1.2.0, 2024-01-15リリース"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">説明（任意）</FormLabel>
                <Textarea
                  size="sm"
                  value={publishDescription}
                  onChange={handlePublishDescriptionChange}
                  placeholder="このリリースの変更内容や目的を記述してください"
                  minHeight="72px"
                  resize="vertical"
                />
              </FormControl>
              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  mb={2}
                  color="gray.700"
                >
                  変更サマリー
                </Text>
                <PendingChangeSummaryLists summary={workspacePendingSummary} />
              </Box>
            </VStack>
          }
          leftColumnLabel={diffColumnLabels.left}
          rightColumnLabel={diffColumnLabels.right}
          leftColumnDescription={null}
          rightColumnDescription={null}
          confirmLabel="変更を公開"
          confirmColorScheme="blue"
          isConfirming={isPublishingWorkspace}
          confirmDisabled={!publishVersionName.trim()}
          isLoadingDiff={false}
          diffLines={diffLines}
          linkedModuleDiffs={linkedModuleDiffs}
          mode="confirm"
          warningText={null}
        />
      )}

      {isCompareDiffOpen && (
        <TagDiffModal
          isOpen
          onClose={onCompareDiffClose}
          title="バージョン比較"
          leftColumnLabel={diffColumnLabels.left}
          rightColumnLabel={diffColumnLabels.right}
          leftColumnDescription={compareDiffDescriptions.left}
          rightColumnDescription={compareDiffDescriptions.right}
          diffLines={diffLines}
          mode={snapshotForRollback ? "confirm" : "info"}
          onConfirm={
            snapshotForRollback ? handleSnapshotDiffRollback : undefined
          }
          confirmLabel={
            snapshotForRollback
              ? `${snapshotForRollback.versionName} にロールバック`
              : undefined
          }
          confirmColorScheme="blue"
          isConfirming={snapshotOperationInProgress === snapshotForRollback?.id}
          isLoadingDiff={false}
          warningText={null}
          stackOnModuleEditor={editingModuleId != null}
          linkedModuleDiffs={historyLinkedModuleDiffs}
          linkedLeftColumnLabel={historyLinkedColumnLabels.left}
          linkedRightColumnLabel={historyLinkedColumnLabels.right}
          linkedModulesHelpText={
            historyLinkedModuleDiffs != null
              ? VERSION_LINKED_DIFF_HELP
              : undefined
          }
        />
      )}

      {isVersionCompareModalOpen && (
        <TagDiffModal
          isOpen
          onClose={onVersionCompareModalClose}
          title="バージョン比較"
          prependBody={
            <VersionComparePanel
              versions={userTagVersionCompareItems}
              getOptionLabel={userTagVersionCompareOptionLabel}
              onShowDiff={versionComparePanelOnShowDiffNoop}
              onSelectionChange={handleVersionCompareSelectionChange}
              hideCompareAction
            />
          }
          leftColumnLabel={versionCompareLabels.left}
          rightColumnLabel={versionCompareLabels.right}
          leftColumnDescription={versionCompareBase?.description ?? null}
          rightColumnDescription={versionCompareTarget?.description ?? null}
          diffLines={versionCompareDiffLines}
          mode="info"
          isLoadingDiff={false}
          warningText={null}
          stackOnModuleEditor={editingModuleId != null}
          linkedModuleDiffs={versionCompareLinkedModuleDiffs}
          linkedLeftColumnLabel={versionCompareLabels.left}
          linkedRightColumnLabel={versionCompareLabels.right}
          linkedModulesHelpText={VERSION_LINKED_DIFF_HELP}
        />
      )}

      {isWorkspaceCancelOpen && (
        <AlertDialog
          isOpen
          leastDestructiveRef={workspaceCancelConfirmRef}
          onClose={onWorkspaceCancelClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>変更を取り消す</AlertDialogHeader>
              <AlertDialogBody>
                <Text mb={3}>
                  以下の保留中の変更をすべて破棄します。この操作は取り消せません。
                </Text>
                <PendingChangeSummaryLists summary={workspacePendingSummary} />
                <Text mt={3} fontSize="sm" color="orange.600">
                  コードの下書きと有効/無効の予約がすべてクリアされます。
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button
                  ref={workspaceCancelConfirmRef}
                  onClick={onWorkspaceCancelClose}
                >
                  キャンセル
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleConfirmWorkspaceCancel}
                  ml={3}
                  isLoading={isDiscardingWorkspace}
                >
                  変更を破棄
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </Box>
  );
};
